import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonText,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonAlert,
  useIonToast,
} from '@ionic/react';
import type { RefresherCustomEvent } from '@ionic/react';
import { createOutline } from 'ionicons/icons';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_LEAGUE_MESSAGES } from '../../../graphql/queries/leagueMessages';
import { UPDATE_MESSAGES_LAST_READ } from '../../../graphql/mutations/updateMessagesLastRead';
import { CREATE_LEAGUE_MESSAGE } from '../../../graphql/mutations/createLeagueMessage';
import { UPDATE_LEAGUE_MESSAGE } from '../../../graphql/mutations/updateLeagueMessage';
import { DELETE_LEAGUE_MESSAGE } from '../../../graphql/mutations/deleteLeagueMessage';
import { GET_FANTASY_LEAGUE } from '../../../graphql/queries/fantasyLeague';
import { GET_MY_FANTASY_LEAGUES } from '../../../graphql/queries/myFantasyLeagues';
import { GET_FANTASY_LEAGUE_TABS_SHELL } from '../../../graphql/queries/fantasyLeagueTabsShell';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';
import type {
  GetLeagueMessagesQuery,
  GetLeagueMessagesQueryVariables,
  UpdateMessagesLastReadMutation,
  UpdateMessagesLastReadMutationVariables,
  CreateLeagueMessageMutation,
  CreateLeagueMessageMutationVariables,
  UpdateLeagueMessageMutation,
  UpdateLeagueMessageMutationVariables,
  DeleteLeagueMessageMutation,
  DeleteLeagueMessageMutationVariables,
  GetFantasyLeagueQuery,
  GetFantasyLeagueQueryVariables,
} from '../../../types/graphql-generated';
import { UserContentTypeEnum } from '../../../types/graphql-generated';
import { LeagueMessageCard } from './components/LeagueMessageCard';
import { ReportContentModal } from '../../components/ReportContentModal';
import { MarkdownComposerModal } from '@/components/markdown';
import { LEAGUE_MESSAGE_MAX_LENGTH } from '../../../appConfig';
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LeagueSettingsButton } from '../components/LeagueSettingsButton';
import { buildReplyQuote } from './utils/replyQuote';

type LeagueMessage = GetLeagueMessagesQuery['leagueMessages'][0];

interface MessagesTabProps {
  leagueId: string;
  memberId?: string;
  messagesLastReadAt?: string | null;
}

const MESSAGES_PER_PAGE = 20;
const MARK_AS_READ_DELAY_MS = 2000;

export const MessagesTab = ({
  leagueId,
  memberId,
  messagesLastReadAt,
}: MessagesTabProps) => {
  const [messagesOffset, setMessagesOffset] = useState(0);
  const readTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isViewActive = pathname === `/app/ligas/${leagueId}/mensajes`;
  const { userAccount } = useUserAccount();
  const currentUserId = userAccount?.id;

  const { isEnabled } = useFeatureFlags();
  const userMessagesEnabled = isEnabled(FeatureFlag.USER_MESSAGES);
  const reportUserContentEnabled = isEnabled(FeatureFlag.REPORT_USER_CONTENT);

  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Id of the message being edited. `null` means the composer creates a new message.
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<LeagueMessage | null>(
    null
  );

  const [messageToReport, setMessageToReport] = useState<LeagueMessage | null>(
    null
  );

  const [presentToast] = useIonToast();

  const { data: messagesData, loading, error, fetchMore, refetch } = useQuery<
    GetLeagueMessagesQuery,
    GetLeagueMessagesQueryVariables
  >(GET_LEAGUE_MESSAGES, {
    variables: {
      fantasyLeagueId: leagueId,
      limit: MESSAGES_PER_PAGE,
      offset: 0,
    },
  });

  // Read the league (already fetched by the parent tabs page) from cache to map each
  // message author to their team name in this league.
  const { data: leagueData } = useQuery<
    GetFantasyLeagueQuery,
    GetFantasyLeagueQueryVariables
  >(GET_FANTASY_LEAGUE, {
    variables: { id: leagueId },
  });

  // Messages can only be reported in public leagues, where users may not know each other.
  const canReportMessages =
    reportUserContentEnabled && leagueData?.fantasyLeague?.isPublic === true;

  const memberByUserId = new Map(
    (leagueData?.fantasyLeague?.members ?? []).map((member) => [member.user.id, member])
  );

  const [updateMessagesLastRead] = useMutation<
    UpdateMessagesLastReadMutation,
    UpdateMessagesLastReadMutationVariables
  >(UPDATE_MESSAGES_LAST_READ, {
    refetchQueries: [
      { query: GET_MY_FANTASY_LEAGUES },
      { query: GET_FANTASY_LEAGUE_TABS_SHELL, variables: { fantasyLeagueId: leagueId } },
    ],
  });

  const [createLeagueMessage, { loading: sending }] = useMutation<
    CreateLeagueMessageMutation,
    CreateLeagueMessageMutationVariables
  >(CREATE_LEAGUE_MESSAGE);

  const [updateLeagueMessage, { loading: updating }] = useMutation<
    UpdateLeagueMessageMutation,
    UpdateLeagueMessageMutationVariables
  >(UPDATE_LEAGUE_MESSAGE);

  const [deleteLeagueMessage] = useMutation<
    DeleteLeagueMessageMutation,
    DeleteLeagueMessageMutationVariables
  >(DELETE_LEAGUE_MESSAGE);

  // Ionic keeps tab pages mounted and does not reliably emit view-enter events
  // with this nested router. The location changes for both direct loads and tab
  // navigation, so use it as the source of truth for whether this view is active.
  useEffect(() => {
    if (!isViewActive || !memberId) return;

    if (readTimerRef.current) {
      clearTimeout(readTimerRef.current);
    }

    readTimerRef.current = setTimeout(() => {
      updateMessagesLastRead({
        variables: {
          input: {
            fantasyLeagueMemberId: memberId,
          },
        },
      }).catch((err) => {
        console.error('Error updating messages last read:', err);
      });
    }, MARK_AS_READ_DELAY_MS);

    return () => {
      if (readTimerRef.current) {
        clearTimeout(readTimerRef.current);
        readTimerRef.current = null;
      }
    };
  }, [isViewActive, memberId, updateMessagesLastRead]);

  const messages = messagesData?.leagueMessages || [];

  const loadMore = async (ev: CustomEvent<void>) => {
    const newOffset = messagesOffset + MESSAGES_PER_PAGE;

    await fetchMore({
      variables: {
        fantasyLeagueId: leagueId,
        limit: MESSAGES_PER_PAGE,
        offset: newOffset,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...prev,
          leagueMessages: [
            ...prev.leagueMessages,
            ...fetchMoreResult.leagueMessages,
          ],
        };
      },
    });

    setMessagesOffset(newOffset);
    (ev.target as HTMLIonInfiniteScrollElement).complete();
  };

  const reloadMessages = async () => {
    setMessagesOffset(0);
    await refetch({
      fantasyLeagueId: leagueId,
      limit: MESSAGES_PER_PAGE,
      offset: 0,
    });
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setText('');
    setErrorMessage(null);
    setEditingMessageId(null);
  };

  const openComposerForNewMessage = () => {
    setEditingMessageId(null);
    setText('');
    setErrorMessage(null);
    setIsOpen(true);
  };

  const openComposerForEdit = (message: LeagueMessage) => {
    setEditingMessageId(message.id);
    setText(message.text);
    setErrorMessage(null);
    setIsOpen(true);
  };

  const openComposerForReply = (message: LeagueMessage) => {
    // Replying creates a brand-new message prefilled with the quoted original.
    setEditingMessageId(null);
    setText(buildReplyQuote(message.text));
    setErrorMessage(null);
    setIsOpen(true);
  };

  const handleSend = async () => {
    setErrorMessage(null);

    const trimmed = text.trim();
    if (!trimmed) return;

    if (editingMessageId) {
      await submitEdit(editingMessageId, trimmed);
      return;
    }

    try {
      const { data } = await createLeagueMessage({
        variables: {
          input: {
            fantasyLeagueId: leagueId,
            text: trimmed,
          },
        },
      });

      const result = data?.createLeagueMessage;
      if (result?.errors && result.errors.length > 0) {
        setErrorMessage(result.errors[0]);
        return;
      }

      setText('');
      setIsOpen(false);
      await reloadMessages();
    } catch (error) {
      console.error('Error creating league message:', error);
      setErrorMessage('No se ha podido enviar el mensaje. Inténtalo de nuevo más tarde.');
    }
  };

  const submitEdit = async (messageId: string, trimmed: string) => {
    try {
      const { data } = await updateLeagueMessage({
        variables: {
          input: {
            leagueMessageId: messageId,
            text: trimmed,
          },
        },
      });

      const result = data?.updateLeagueMessage;
      if (result?.errors && result.errors.length > 0) {
        setErrorMessage(result.errors[0]);
        return;
      }

      setText('');
      setIsOpen(false);
      setEditingMessageId(null);
      await reloadMessages();
    } catch (error) {
      console.error('Error updating league message:', error);
      setErrorMessage('No se ha podido editar el mensaje. Inténtalo de nuevo más tarde.');
    }
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;

    const messageId = messageToDelete.id;
    setMessageToDelete(null);

    try {
      const { data } = await deleteLeagueMessage({
        variables: { input: { leagueMessageId: messageId } },
      });

      const result = data?.deleteLeagueMessage;
      if (result?.errors && result.errors.length > 0) {
        await presentToast({
          message: result.errors[0],
          duration: 3000,
          color: 'danger',
        });
        return;
      }

      await reloadMessages();
    } catch (error) {
      console.error('Error deleting league message:', error);
      await presentToast({
        message: 'No se ha podido borrar el mensaje. Inténtalo de nuevo más tarde.',
        duration: 3000,
        color: 'danger',
      });
    }
  };

  const onRefresh = async (ev: RefresherCustomEvent) => {
    try {
      await reloadMessages();
    } finally {
      ev.detail.complete();
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/mis-ligas" />
          </IonButtons>
          <IonTitle className="ml-4">Mensajes</IonTitle>
          <IonButtons slot="end">
            <LeagueSettingsButton leagueId={leagueId} />
          </IonButtons>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {userMessagesEnabled && (
          <>
            <IonFab vertical="bottom" horizontal="end" slot="fixed">
              <IonFabButton
                aria-label="Escribir un mensaje"
                onClick={openComposerForNewMessage}
              >
                <IonIcon icon={createOutline} />
              </IonFabButton>
            </IonFab>

            <MarkdownComposerModal
              isOpen={isOpen}
              onDismiss={handleDismiss}
              value={text}
              onChange={setText}
              onSubmit={handleSend}
              loading={sending || updating}
              errorMessage={errorMessage}
              placeholder="Escribe tu mensaje en markdown"
              maxLength={LEAGUE_MESSAGE_MAX_LENGTH}
              submitLabel={editingMessageId ? 'Guardar' : 'Enviar'}
            />

            <IonAlert
              isOpen={messageToDelete !== null}
              header="Eliminar mensaje"
              message="¿Seguro que quieres eliminar este mensaje? Esta acción no se puede deshacer."
              onDidDismiss={() => setMessageToDelete(null)}
              buttons={[
                {
                  text: 'Cancelar',
                  role: 'cancel',
                  handler: () => setMessageToDelete(null),
                },
                {
                  text: 'Eliminar',
                  role: 'destructive',
                  handler: () => {
                    void confirmDelete();
                  },
                },
              ]}
            />
          </>
        )}

        {canReportMessages && (
          <ReportContentModal
            isOpen={messageToReport !== null}
            onClose={() => setMessageToReport(null)}
            contentType={UserContentTypeEnum.LeagueMessage}
            contentId={messageToReport?.id ?? ''}
          />
        )}

        {loading && messages.length === 0 && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar los mensajes</p>
          </IonText>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonText color="medium">
              <p>No hay mensajes en esta liga</p>
            </IonText>
          </div>
        )}

        {messages.length > 0 && (
          <>
            <div>
              {messages.map((message) => {
                const authorMember = message.user
                  ? memberByUserId.get(message.user.id)
                  : undefined;

                return (
                  <LeagueMessageCard
                    key={message.id}
                    message={message}
                    messagesLastReadAt={messagesLastReadAt}
                    authorTeamName={authorMember?.teamName}
                    canManage={
                      userMessagesEnabled &&
                      !!message.user &&
                      !!currentUserId &&
                      message.user.id === currentUserId
                    }
                    canReply={
                      userMessagesEnabled &&
                      !!message.user &&
                      !!currentUserId &&
                      message.user.id !== currentUserId
                    }
                    canReport={
                      canReportMessages &&
                      !!message.user &&
                      !!currentUserId &&
                      message.user.id !== currentUserId
                    }
                    onAuthorClick={
                      authorMember
                        ? () => navigate(
                            `/app/ligas/${leagueId}/mensajes/plantilla/${authorMember.id}`
                          )
                        : undefined
                    }
                    onEdit={openComposerForEdit}
                    onDelete={setMessageToDelete}
                    onReply={openComposerForReply}
                    onReport={setMessageToReport}
                  />
                );
              })}
            </div>

            <IonInfiniteScroll onIonInfinite={loadMore} threshold="100px">
              <IonInfiniteScrollContent
                loadingSpinner="bubbles"
                loadingText="Cargando más..."
              />
            </IonInfiniteScroll>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
